import React, { useEffect, useState, useRef, useCallback } from 'react'
import {
  Dialog,
  DialogContent,
  Slide,
  IconButton,
  Snackbar,
  Alert,
  CircularProgress,
} from '@mui/material'
import { FiX, FiCheckCircle } from 'react-icons/fi'
import api from '@/services/api'
import { useNavigate } from 'react-router-dom'

const Transition = React.forwardRef(function Transition(props: any, ref) {
  return <Slide direction="up" ref={ref} {...props} />
})

interface DocuSignSignerProps {
  contractId: string
  open: boolean
  onClose: () => void
  onSigned: () => void
  fromBooking?: boolean
}

const DocuSignSigner: React.FC<DocuSignSignerProps> = ({
  contractId,
  open,
  onClose,
  onSigned,
  fromBooking = false,
}) => {
  const navigate = useNavigate()
  const [signingUrl, setSigningUrl] = useState<string>('')
  const [loading, setLoading] = useState(true)
  const [submitting, setSubmitting] = useState(false)
  const [showSuccess, setShowSuccess] = useState(false)
  const [errorMessage, setErrorMessage] = useState<string>('')

  // Track iframe loads - first load is signing page, second load is completion page
  const iframeLoadCountRef = useRef(0)
  const hasSubmittedRef = useRef(false)

  // Auto-submit when signing is detected
  const handleAutoSubmit = useCallback(async () => {
    // Prevent duplicate submissions
    if (hasSubmittedRef.current || submitting) return
    hasSubmittedRef.current = true

    console.log('✅ Signing complete - auto-submitting...')
    setSubmitting(true)

    try {
      await api.get(`/contracts/patient-sign/${contractId}`)
      setShowSuccess(true)

      setTimeout(() => {
        onSigned()
        onClose()
        if (fromBooking) {
          navigate('/contracts?fromBooking=true&tab=pending')
        }
      }, 1500)
    } catch (error) {
      console.error('Failed to confirm contract signature:', error)
      setErrorMessage(
        'Unable to confirm signature. Please try again or contact support.'
      )
      setSubmitting(false)
      hasSubmittedRef.current = false
    }
  }, [contractId, fromBooking, navigate, onClose, onSigned, submitting])

  // Handle iframe load events - detect when DocuSign navigates to success page
  const handleIframeLoad = useCallback(() => {
    iframeLoadCountRef.current += 1
    console.log(`📄 Iframe loaded (count: ${iframeLoadCountRef.current})`)

    // First load is the initial signing page
    // Second load (or more) means user navigated - likely to completion page
    if (iframeLoadCountRef.current >= 2) {
      handleAutoSubmit()
    }
  }, [handleAutoSubmit])

  // Listen for DocuSign postMessage events (backup detection)
  useEffect(() => {
    if (!open) return

    const handleMessage = (event: MessageEvent) => {
      const data = event.data

      // Check for string messages
      if (typeof data === 'string') {
        if (
          data.includes('signing_complete') ||
          data.includes('signingComplete') ||
          data.includes('Signing Complete')
        ) {
          handleAutoSubmit()
          return
        }
      }

      // Check for object messages
      if (typeof data === 'object' && data !== null) {
        const eventType = data.event || data.type || data.action || ''
        const status = data.status || ''

        if (
          eventType === 'signing_complete' ||
          eventType === 'signingComplete' ||
          status === 'signing_complete' ||
          data.signingComplete === true
        ) {
          handleAutoSubmit()
        }
      }
    }

    window.addEventListener('message', handleMessage)
    return () => window.removeEventListener('message', handleMessage)
  }, [open, handleAutoSubmit])

  // API to get signing URL
  const fetchSigningUrl = async () => {
    try {
      const { data } = await api.post('/docusign/get-signurl', {
        contractId,
        type: 'patient',
      })

      console.log('📩 Loaded signing URL:', data.signingUrl)
      setSigningUrl(data.signingUrl)
    } catch (err) {
      console.error('❌ Failed to load DocuSign URL', err)
      setErrorMessage('Failed to load signing document. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  const handleClose = () => {
    if (loading || submitting) return
    onClose()
  }

  useEffect(() => {
    if (open) {
      // Reset all states when modal opens
      setShowSuccess(false)
      setSubmitting(false)
      setErrorMessage('')
      setLoading(true)
      setSigningUrl('')
      iframeLoadCountRef.current = 0
      hasSubmittedRef.current = false
      fetchSigningUrl()
    }
  }, [open])

  return (
    <>
      {/* SUCCESS POPUP */}
      {/* <Snackbar
        open={showSuccess}
        autoHideDuration={2000}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          severity="success"
          className="!bg-gradient-to-r !from-green-100 !to-green-50 !text-green-700 !rounded-xl !shadow-lg !border !border-green-200"
          icon={false}
        >
          <div className="flex items-center gap-3">
            <div className="flex items-center justify-center text-lg text-white bg-green-600 rounded-full w-7 h-7">
              ✓
            </div>
            <span className="font-semibold">Contract signed successfully!</span>
          </div>
        </Alert>
      </Snackbar> */}

      {/* ERROR POPUP */}
      <Snackbar
        open={!!errorMessage}
        autoHideDuration={5000}
        onClose={() => setErrorMessage('')}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="error" onClose={() => setErrorMessage('')}>
          {errorMessage}
        </Alert>
      </Snackbar>

      {/* SIGNING DIALOG */}
      <Dialog
        fullWidth
        maxWidth={false}
        open={open}
        onClose={handleClose}
        TransitionComponent={Transition}
        style={{ zIndex: 9999999 }}
        PaperProps={{
          style: {
            maxWidth: '90vw',
            maxHeight: '90vh',
            width: '90vw',
            height: '90vh',
            margin: 'auto',
          },
          className:
            'rounded-2xl shadow-xl border border-gray-200 !overflow-hidden flex flex-col',
        }}
      >
        {/* Header */}
        <div className="flex items-center justify-between flex-shrink-0 px-5 py-4 border-b border-gray-100 bg-gray-50">
          <h2 className="text-lg font-semibold text-gray-800">Sign Contract</h2>
          <IconButton onClick={handleClose} disabled={loading || submitting}>
            <FiX className="text-gray-500" />
          </IconButton>
        </div>

        <DialogContent className="flex flex-col flex-1 min-h-0 p-0 overflow-hidden">
          {/* Loading State */}
          {loading && (
            <div className="flex flex-col items-center justify-center flex-1 gap-4 text-gray-500">
              <CircularProgress size={40} />
              <span>Loading contract document...</span>
            </div>
          )}

          {/* Submitting State - Auto-submitting after signing complete */}
          {!loading && submitting && (
            <div className="flex flex-col items-center justify-center flex-1 p-8 bg-gradient-to-b from-green-50 to-white">
              <div className="flex items-center justify-center w-20 h-20 mb-6 bg-green-500 rounded-full shadow-lg">
                <FiCheckCircle className="text-4xl text-white" />
              </div>
              <h3 className="mb-3 text-2xl font-semibold text-green-700">
                Signing Complete!
              </h3>
              <p className="max-w-md mb-6 text-center text-gray-600">
                Please wait while we confirm your signature...
              </p>
              <CircularProgress size={32} sx={{ color: '#10b981' }} />
            </div>
          )}

          {/* DocuSign iframe - Show when signing */}
          {!loading && !submitting && signingUrl && (
            <iframe
              src={signingUrl}
              className="flex-1 w-full border-0"
              frameBorder="0"
              title="DocuSign Contract"
              onLoad={handleIframeLoad}
              style={{ minHeight: 0 }}
            />
          )}
        </DialogContent>

        {/* Footer - Only show when signing is in progress */}
        {!loading && !submitting && (
          <div className="flex-shrink-0 px-4 py-3 border-t border-gray-100 bg-gray-50">
            <span className="text-sm text-gray-500">
              Complete signing in the document above and click "Finish".
            </span>
          </div>
        )}
      </Dialog>
    </>
  )
}

export default DocuSignSigner
